import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, Circle, Download, ExternalLink, Minus, RefreshCw } from 'lucide-react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import apiKeysService, {
  DEFAULT_PUBLIC_API_URL,
  REQUEST_LOG_PAGE_SIZE,
  REQUEST_LOG_RETENTION_DAYS,
  integrationInfoFor,
  type ApiIntegrationInfo,
  type ApiKey,
  type ApiKeyRequestLogEntry,
} from '@services/api-keys.service';
import { integrationService, type WebhookDelivery } from '@services/integration.service';
import { teamsService } from '@services/teams.service';
import { useAuthStore } from '@stores/authStore';
import {
  GO_LIVE_ITEMS,
  buildGuide,
  missingScopes,
  type GuideContext,
  type GuideTeam,
  type SampleLang,
} from './guideContent';
import { CHECK_LOG_PAGES, countDone, evaluateChecks, type CheckInputs, type CheckResults } from './guideChecks';
import { guideFileName, guideToMarkdown } from './guideMarkdown';
import { CopyButton, GuideBlockView, InlineText } from './GuideBlocks';

const LANG_STORAGE_KEY = 'centraone.integrationGuide.lang';
const LANGS: SampleLang[] = ['curl', 'node', 'python', 'csharp'];
/** Deliveries are read for at most this many enabled webhooks. */
const MAX_WEBHOOKS_CHECKED = 5;

function readStoredLang(): SampleLang {
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY) as SampleLang | null;
    if (stored && LANGS.includes(stored)) return stored;
  } catch {
    // storage unavailable: fall back to the default
  }
  return 'curl';
}

/** The key the guide opens on: an active key with a product, else any active key. */
export function defaultGuideKey(keys: ApiKey[]): ApiKey | undefined {
  const active = keys.filter((key) => key.isActive);
  return active.find((key) => key.productSlug) ?? active[0];
}

async function loadCheckInputs(keyId: string, canViewLog: boolean): Promise<CheckInputs> {
  const loadRequests = async (): Promise<ApiKeyRequestLogEntry[] | null> => {
    if (!canViewLog) return null;
    try {
      const requests: ApiKeyRequestLogEntry[] = [];
      let before: string | undefined;
      for (let page = 0; page < CHECK_LOG_PAGES; page += 1) {
        const result = await apiKeysService.getApiKeyRequests(keyId, {
          limit: REQUEST_LOG_PAGE_SIZE,
          before,
        });
        requests.push(...result.data);
        if (!result.nextBefore) break;
        before = result.nextBefore;
      }
      return requests;
    } catch {
      return null;
    }
  };

  const loadWebhooks = async (): Promise<Pick<CheckInputs, 'webhooks' | 'deliveries'>> => {
    try {
      const webhooks = await integrationService.getWebhooks();
      const enabled = webhooks.filter((webhook) => webhook.isEnabled).slice(0, MAX_WEBHOOKS_CHECKED);
      const settled = await Promise.allSettled(
        enabled.map((webhook) => integrationService.getWebhookDeliveries(webhook.id)),
      );
      const read = settled.filter(
        (result): result is PromiseFulfilledResult<WebhookDelivery[]> => result.status === 'fulfilled',
      );
      const deliveries =
        enabled.length > 0 && read.length === 0 ? null : read.flatMap((result) => result.value ?? []);
      return { webhooks, deliveries };
    } catch {
      return { webhooks: null, deliveries: null };
    }
  };

  const [requests, hooks] = await Promise.all([loadRequests(), loadWebhooks()]);
  return { requests, ...hooks };
}

function GoLiveChecklist({
  selectedKey,
  results,
  loading,
  canViewLog,
  onRefresh,
}: {
  selectedKey: ApiKey | undefined;
  results: CheckResults | null;
  loading: boolean;
  canViewLog: boolean;
  onRefresh: () => void;
}) {
  const tracked = GO_LIVE_ITEMS.filter((item) => item.checkId).length;

  return (
    <div className="space-y-3">
      {selectedKey ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600 dark:text-gray-400">
          <p>
            {results
              ? `${countDone(results)} of ${tracked} confirmed for "${selectedKey.name}".`
              : loading
                ? 'Checking this key\'s activity…'
                : 'Progress could not be read.'}{' '}
            {canViewLog
              ? `Based on its last ${CHECK_LOG_PAGES * REQUEST_LOG_PAGE_SIZE} requests (kept ${REQUEST_LOG_RETENTION_DAYS} days) and this tenant's webhooks.`
              : 'Request activity is visible to admins only, so only the webhook items are checked.'}
          </p>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      ) : (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Choose a key at the top of the guide to see which of these CentraOne can already confirm.
        </p>
      )}

      <ul className="divide-y divide-gray-100 dark:divide-gray-700/60 rounded-lg border border-gray-200 dark:border-gray-700">
        {GO_LIVE_ITEMS.map((item) => {
          const state = item.checkId && selectedKey && results ? results[item.checkId] : null;
          return (
            <li key={item.text} className="flex items-start gap-3 px-3 py-2.5 text-sm">
              <span className="mt-0.5 flex-shrink-0">
                {state === 'done' ? (
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden />
                ) : state === 'pending' ? (
                  <Circle className="h-4 w-4 text-gray-400" aria-hidden />
                ) : (
                  <Minus className="h-4 w-4 text-gray-300 dark:text-gray-600" aria-hidden />
                )}
              </span>
              <span className="flex-1 text-gray-700 dark:text-gray-300">
                <InlineText text={item.text} />
              </span>
              <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                {state === 'done'
                  ? 'Confirmed'
                  : state === 'pending'
                    ? 'Not seen yet'
                    : item.checkId
                      ? selectedKey && !loading
                        ? 'Cannot check'
                        : ''
                      : 'Check yourself'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function IntegrationGuide({
  apiKeys,
  apiKeysEntitled,
  requiredPlan,
}: {
  apiKeys: ApiKey[];
  apiKeysEntitled: boolean;
  requiredPlan: string;
}) {
  const { user } = useAuthStore();
  const canViewLog = apiKeysService.canViewRequestLog(user?.role);

  const [info, setInfo] = useState<ApiIntegrationInfo | null>(null);
  const [teams, setTeams] = useState<GuideTeam[] | null>(null);
  const [lang, setLang] = useState<SampleLang>(readStoredLang);
  // undefined = not chosen yet (use the default key); '' = "no key".
  const [selectedKeyId, setSelectedKeyId] = useState<string | undefined>(undefined);
  const [results, setResults] = useState<CheckResults | null>(null);
  const [checksLoading, setChecksLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiKeysService.getIntegrationInfo().then((value) => {
      if (!cancelled) setInfo(value);
    });
    teamsService
      .getTeams()
      .then((response) => {
        if (cancelled) return;
        const list = Array.isArray(response?.data) ? response.data : [];
        setTeams(
          list
            .filter((team) => team.isActive !== false)
            .map((team) => ({ id: team.id, name: team.name })),
        );
      })
      .catch(() => {
        if (!cancelled) setTeams(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedKey =
    selectedKeyId === undefined
      ? defaultGuideKey(apiKeys)
      : apiKeys.find((key) => key.id === selectedKeyId);
  const selectedId = selectedKey?.id;

  const refreshChecks = useCallback(async () => {
    if (!selectedId) {
      setResults(null);
      return;
    }
    setChecksLoading(true);
    try {
      setResults(evaluateChecks(await loadCheckInputs(selectedId, canViewLog)));
    } finally {
      setChecksLoading(false);
    }
  }, [selectedId, canViewLog]);

  useEffect(() => {
    setResults(null);
    refreshChecks();
  }, [refreshChecks]);

  const handleLangChange = (next: SampleLang) => {
    setLang(next);
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      // not persisted; the choice still applies to this visit
    }
  };

  const ctx: GuideContext = useMemo(() => {
    const resolved = info ?? integrationInfoFor(DEFAULT_PUBLIC_API_URL);
    return {
      baseUrl: resolved.apiBaseUrl,
      apiReferenceUrl: resolved.apiReferenceUrl,
      openApiUrl: resolved.openApiUrl,
      keyName: selectedKey?.name ?? null,
      productName: selectedKey?.productName ?? null,
      productSlug: selectedKey?.productSlug ?? null,
      keyRateLimit: selectedKey?.rateLimit ?? null,
      teams,
    };
  }, [info, selectedKey, teams]);

  const sections = useMemo(() => buildGuide(ctx), [ctx]);

  const handleDownload = () => {
    const blob = new Blob([guideToMarkdown(ctx)], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = guideFileName(ctx);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  if (!info) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!apiKeysEntitled && (
        <Card className="p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              API access is included in the {requiredPlan} plan and higher. You can read the guide
              now; calls will work once the plan is upgraded and a key is issued.
            </p>
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Everything a developer needs to connect another system. They have no CentraOne login,
              so download the guide and send it to them. The API key is never included.
            </p>
            <div className="flex flex-wrap items-center gap-x-1 gap-y-2 text-sm">
              <span className="text-gray-500 dark:text-gray-400">Base URL</span>
              <code className="ml-1 font-mono text-gray-900 dark:text-white break-all">{info.apiBaseUrl}</code>
              <CopyButton value={info.apiBaseUrl} label="Base URL" />
              <a
                href={info.apiReferenceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
              >
                API reference
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </div>
          </div>
          <Button onClick={handleDownload}>
            <Download className="h-4 w-4 mr-2" />
            Download guide (.md)
          </Button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/60">
          <label
            htmlFor="integration-guide-key"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Show the guide for
          </label>
          <select
            id="integration-guide-key"
            value={selectedKey?.id ?? ''}
            onChange={(event) => setSelectedKeyId(event.target.value)}
            className="w-full max-w-md rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
          >
            <option value="">No key (generic examples)</option>
            {apiKeys.map((key) => (
              <option key={key.id} value={key.id}>
                {key.name}
                {key.productName ? ` (${key.productName})` : ''}
                {key.isActive ? '' : ' (disabled)'}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {selectedKey
              ? selectedKey.productSlug
                ? `Examples use this key's ticket source, ${selectedKey.productSlug}.`
                : 'This key has no product, so examples send a placeholder externalSource.'
              : 'Examples use placeholder values.'}
          </p>
        </div>
      </Card>

      <div className="lg:grid lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-8">
        <nav aria-label="Guide sections" className="hidden lg:block">
          <ul className="sticky top-4 space-y-1 text-sm">
            {sections.map((section, index) => (
              <li key={section.id}>
                <a
                  href={`#guide-${section.id}`}
                  className="block rounded px-2 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"
                >
                  {index + 1}. {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-10 min-w-0">
          {sections.map((section, index) => {
            const missing = missingScopes(section, selectedKey?.permissions);
            return (
              <section
                key={section.id}
                id={`guide-${section.id}`}
                aria-labelledby={`guide-${section.id}-title`}
                className="space-y-3 scroll-mt-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <h3
                    id={`guide-${section.id}-title`}
                    className="text-base font-semibold text-gray-900 dark:text-white"
                  >
                    {index + 1}. {section.title}
                  </h3>
                  {missing.length > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
                      <AlertTriangle className="h-3 w-3" aria-hidden />
                      This key lacks {missing.join(', ')}
                    </span>
                  )}
                </div>
                {section.blocks.map((block, blockIndex) => (
                  <GuideBlockView
                    key={blockIndex}
                    block={block}
                    lang={lang}
                    onLangChange={handleLangChange}
                    checklist={
                      <GoLiveChecklist
                        selectedKey={selectedKey}
                        results={results}
                        loading={checksLoading}
                        canViewLog={canViewLog}
                        onRefresh={refreshChecks}
                      />
                    }
                  />
                ))}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
